import { useCallback, useRef, useState } from "react";
import { useDataProvider } from "ra-core";

type TaskRecord = Record<string, unknown> & {
  text?: unknown;
  match_basis?: "auto" | "manual";
  offset_seconds?: number | null;
  match_quote?: string;
};

interface MatchResult {
  i: number;
  offset_seconds: number | null;
  quote?: string;
}

// Apply matcher results back onto the tasks. Tasks the user set by hand
// (match_basis === "manual") are preserved — a re-match never clobbers them.
function withMatches(
  tasks: TaskRecord[],
  results: MatchResult[],
): TaskRecord[] {
  const byIndex = new Map(results.map((r) => [r.i, r]));
  return tasks.map((t, i) => {
    if (t.match_basis === "manual") return t;
    const r = byIndex.get(i);
    if (!r) return t;
    return {
      ...t,
      offset_seconds: r.offset_seconds,
      match_basis: "auto" as const,
      match_quote: r.quote ?? "",
    };
  });
}

export function useTimestampMatching(meetingId: number | undefined) {
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  // Guards against a second Gemini call firing while one is in flight (the
  // paid matcher runs for tens of seconds — a duplicate would double the cost).
  const inFlightRef = useRef(false);

  const persist = useCallback(
    async (tasks: TaskRecord[]) => {
      if (!meetingId) return;
      try {
        await dataProvider.update("zoom_meetings", {
          id: meetingId,
          data: { tasks },
          previousData: { id: meetingId },
        });
      } catch (err) {
        console.warn("[timestamp-matching] persist failed", err);
      }
    },
    [dataProvider, meetingId],
  );

  const match = useCallback(
    async (
      tasks: TaskRecord[],
      vttContent: string,
      onUpdate: (tasks: TaskRecord[]) => void,
    ) => {
      if (!tasks.length || !vttContent) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setLoading(true);
      setProgress("Matching…");
      try {
        // Imported lazily so the Supabase client (which needs VITE_SUPABASE_URL
        // at construction) isn't pulled into this module at load time — that
        // would break unit tests that import task components without that env.
        const { supabase } = await import("../providers/supabase/supabase");
        const { data, error } = await supabase.functions.invoke<{
          results: MatchResult[];
        }>("match_timestamps", {
          method: "POST",
          body: {
            tasks: tasks.map((t) => ({ text: t.text })),
            vtt: vttContent,
          },
        });
        if (error) {
          console.warn("[timestamp-matching] match_timestamps failed", error);
          return;
        }
        const results = data?.results ?? [];
        const matched = withMatches(tasks, results);
        onUpdate(matched);
        await persist(matched);
      } catch (err) {
        console.warn("[timestamp-matching] matching errored", err);
      } finally {
        inFlightRef.current = false;
        setLoading(false);
        setProgress("");
      }
    },
    [persist],
  );

  return { match, loading, progress };
}
